import { type FC, memo, useCallback } from "react";
import dayjs from "dayjs";
import { SelectChip, SelectMenuOption, SelectMenuSelectionMode } from "@stubhub/broadway-web";
import type { EventType } from "src/http/api-types";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { TimeOfDayFilterOptions } from "src/shared/enums/enums";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { usePerformerPageModelData } from "src/website-apps/responsive/stubhub-performer/hooks/usePerformerPageModelData";
import { BroadwayMedianPriceFilterChip } from "src/website-apps/shared/components/event-filters/BroadwayMedianPriceFilterChip";
import { EventTypeFilterChip } from "src/website-apps/shared/components/event-filters/EventTypeFilterChip";
import { useEventsGridContext } from "src/website-apps/shared/context/events-grid-context";
import { useLocaleDayjs } from "src/website-apps/shared/hooks/useLocaleDayjs";
import { useTimeFilterOptions } from "../hooks/useTimeFilterOptions";
import { filterBarStyle, timeChipUnselectedClasses } from "./ResidencyDesktopFilterBar.styles";

interface Props {
  availableMonths?: Date[];
  currentMonth?: Date | null;
  onMonthSelect?: (month: Date) => void;
}

export const ResidencyDesktopFilterBar: FC<Props> = memo(({ availableMonths, currentMonth, onMonthSelect }) => {
  const storePageEvent = useStorePageEvent();
  const [state, dispatch, { setTimeOfDayFilter, setGridFilterType }] = useEventsGridContext();
  const model = usePerformerPageModelData();
  const timeOptions = useTimeFilterOptions();

  const localeDayjs = useLocaleDayjs();

  const selectedTime = state.filters.timeOfDayType ?? TimeOfDayFilterOptions.ALL;
  const selectedTimeLabel = timeOptions.find((o) => o.value === selectedTime)?.label ?? timeOptions[0]?.label ?? "";

  const handleTimeChange = useCallback(
    (values: string[]) => {
      const raw = values[0];
      const num = raw === undefined ? Number.NaN : Number(raw);
      const value = Number.isNaN(num)
        ? TimeOfDayFilterOptions.ALL
        : ((num as (typeof TimeOfDayFilterOptions)[keyof typeof TimeOfDayFilterOptions]) ?? TimeOfDayFilterOptions.ALL);
      setTimeOfDayFilter(dispatch, state, value);
      const selectedIndex = timeOptions.findIndex((o) => o.value === value);
      storePageEvent(PE_TYPE.CLICK, PE_KEYS.RESIDENCY_CALENDAR_TIME_FILTER_CLICK, String(selectedIndex));
    },
    [dispatch, state, setTimeOfDayFilter, timeOptions, storePageEvent],
  );

  const handleFilterTypeChange = useCallback(
    (val: EventType) => {
      setGridFilterType(dispatch, state, val);
    },
    [dispatch, state, setGridFilterType],
  );

  const handleMonthYearChipChange = useCallback(
    (values: string[]) => {
      if (!onMonthSelect || !availableMonths) return;
      const key = values[0];
      // Intentionally not useLocaleDayjs — digit-only month key, not display text.
      const targetIndex = availableMonths.findIndex((m) => dayjs(m).format("YYYY-MM") === key);
      if (targetIndex !== -1) {
        onMonthSelect(availableMonths[targetIndex]!);
        storePageEvent(
          PE_TYPE.CLICK,
          PE_KEYS.RESIDENCY_CALENDAR_DATE_FILTER_CLICK,
          `${String(targetIndex)}/${String(availableMonths.length)}`,
        );
      }
    },
    [availableMonths, onMonthSelect, storePageEvent],
  );

  const showMonthYearPicker =
    currentMonth !== null &&
    currentMonth !== undefined &&
    !!availableMonths?.length &&
    onMonthSelect !== null &&
    onMonthSelect !== undefined;

  return (
    <div className={filterBarStyle}>
      <BroadwayMedianPriceFilterChip />
      <SelectChip
        label={selectedTimeLabel}
        onChange={handleTimeChange}
        selectionMode={SelectMenuSelectionMode.SingleSelect}
        value={selectedTime === TimeOfDayFilterOptions.ALL ? [] : [String(selectedTime)]}
        {...(selectedTime === TimeOfDayFilterOptions.ALL ? { classes: timeChipUnselectedClasses } : {})}
      >
        {timeOptions.map(({ label, value }) => (
          <SelectMenuOption key={value} value={String(value)} label={label} />
        ))}
      </SelectChip>
      {showMonthYearPicker && (
        <SelectChip
          label={localeDayjs(currentMonth).format("MMMM YYYY")}
          onChange={handleMonthYearChipChange}
          selectionMode={SelectMenuSelectionMode.SingleSelect}
          // Intentionally not useLocaleDayjs — digit-only month key, not display text.
          value={[dayjs(currentMonth).format("YYYY-MM")]}
        >
          {availableMonths.map((m) => (
            <SelectMenuOption
              // Intentionally not useLocaleDayjs — digit-only month key, not display text.
              key={dayjs(m).format("YYYY-MM")}
              value={dayjs(m).format("YYYY-MM")}
              label={localeDayjs(m).format("MMMM YYYY")}
            />
          ))}
        </SelectChip>
      )}
      <EventTypeFilterChip
        onChange={handleFilterTypeChange}
        options={model.gridFilterTypes ?? []}
        value={state.filters.gridFilterType}
      />
    </div>
  );
});
ResidencyDesktopFilterBar.displayName = "ResidencyDesktopFilterBar";
